import { useMemo } from 'react';
import Head from 'next/head';
import { getProductionDomainFromLocale } from '$components/pages/util/domain';
import { FACEBOOK_URL, TWITTER_CREATOR, TWITTER_SITE } from '$constants/openGraphDefaults';
import { FONT_PRELOADS } from '$constants/font-preloads';
import { metaRobotsValue } from '$util/metaRobotsValue';
import { capitalize } from '$util/basic/capitalize';
import { useAppContext } from '$util/AppContext';
import { ResourceHints } from '$src-components/atoms/ResourceHints';

const setRobots = (robots) => (robots ? 'index, follow' : 'noindex, nofollow');

const generateCrumbs = (items, baseURL, productionDomain) => {
  const listItems = items.map((item, i) => {
    const newPath = i === 0 ? item : items.slice(0, i + 1).join('/');

    return {
      '@type': 'ListItem',
      position: `${i + 1}`,
      item: {
        '@type': 'WebPage',
        '@id': `https://${productionDomain}/${newPath}`,
        url: `https://${productionDomain}/${newPath}`,
        name: item,
      },
    };
  });

  return {
    '@type': 'BreadcrumbList',
    '@id': baseURL,
    itemListElement: listItems,
  };
};

const generateStructuredData = ({
  baseTitle,
  baseDescription,
  baseURL,
  items,
  schemaFAQs,
  productionDomain,
}) => {
  let StructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `https://${productionDomain}`,
        name: 'Fixter',
        url: `https://${productionDomain}`,
        sameAs: [
          'https://www.facebook.com/hellofixter',
          'https://twitter.com/hellofixter',
          'https://www.linkedin.com/company/hellofixter',
        ],
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'London',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.5',
          reviewCount: '1521',
        },
        description:
          'Looking for hassle-free MOT, car service, repair or maintenance?' +
          ' Our local experts will collect and deliver your car to your ' +
          'door and make sure you get the right job done at a fair price.',
        telephone: '+44 800 368 8632',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+44 800 368 8632',
          contactType: 'Customer Support',
          areaServed: 'GB',
          availableLanguage: 'English',
        },
        logo: {
          '@type': 'ImageObject',
          '@id': `https://${productionDomain}`,
          url: 'https://res.cloudinary.com/fxtr/image/upload/v1622815951/landing/icon_fixter-logo_kdhluo.svg',
          caption: 'Fixter',
        },
        image: {
          '@id': `https://${productionDomain}`,
        },
      },
      {
        '@type': 'WebSite',
        '@id': `https://${productionDomain}`,
        publisher: {
          '@id': `https://${productionDomain}`,
        },
        name: 'Fixter',
        url: `https://${productionDomain}/`,
        description:
          'Looking for hassle-free MOT, car service, repair or maintenance?' +
          ' Our local experts will collect and deliver your car to your ' +
          'door and make sure you get the right job done at a fair price.',
        inLanguage: 'en-US',
      },
      {
        '@type': 'WebPage',
        '@id': baseURL,
        url: baseURL,
        inLanguage: 'en-US',
        name: baseTitle,
        isPartOf: {
          '@id': `https://${productionDomain}`,
        },
        about: {
          '@id': `https://${productionDomain}`,
        },
        description: baseDescription,
        breadcrumb: {
          '@id': baseURL,
        },
      },
    ],
  };

  if (items && items.length) StructuredData['@graph'].push(generateCrumbs(items, baseURL, productionDomain));
  if (schemaFAQs && schemaFAQs.mainEntity.length) {
    StructuredData = {
      ...StructuredData,
      '@type': 'FAQPage',
      ...schemaFAQs,
    };
  }

  return StructuredData;
};

/**
 * @todo proper implementation after reviewing this strategy with Marketing.
 * @note: hrefLang should also self-reference
 *
 * The more targeted we go with HREFLANG targeting, the less matches we
 * have with general browser and language settings.
 * https://salt.agency/blog/multilingual-seo-locales-regions-single-country/?utm_source=pocket_mylist
 */
const getAlternateLinks = (pathname) => {
  if (pathname === '/')
    return [
      {
        hrefLang: 'en',
        href: 'https://www.fixter.co.uk',
      },
      {
        hrefLang: 'fr',
        href: 'https://www.fixter.fr',
      },
    ];
  return [];
};

export default function MainHead({
  seo = {},
  pageMeta = {},
  openGraph = {},
  domainVerification = {},
  locale,
}) {
  const { location } = useAppContext();
  const { title, description, canonical, robots, keywords, geoMeta, areaName, types, schemaFAQs, pathname } =
    pageMeta;
  const {
    facebookUrl = FACEBOOK_URL,
    twitterSite = TWITTER_SITE,
    twitterCreator = TWITTER_CREATOR,
  } = openGraph;

  const { facebook } = domainVerification;

  const productionDomain = getProductionDomainFromLocale(locale);

  const snippet = generateStructuredData({
    baseTitle: title,
    description,
    baseURL: canonical,
    items: (types || []).map((tp) => tp.slug),
    schemaFAQs,
    productionDomain,
  });

  const alternateLinks = getAlternateLinks(pathname);

  const seoMeta = useMemo(() => {
    const { noindex, nofollow, title: seoTitle, metaDescription, metaKeywords } = seo;

    /**
     * Only apply robots rules if this is in fact a production domain.
     * Otherwise, always noindex and nofollow
     *
     * @note the CMS is not the only source of SEO values so if noindex or nofollow are not
     * booleans (i.e. have values from CMS), fallback to getting the value with setRobots.
     */
    let robotsValue;
    if (location.hostname === productionDomain) {
      robotsValue =
        typeof noindex === 'boolean' || typeof nofollow === 'boolean'
          ? metaRobotsValue({ noindex, nofollow })
          : '';
    } else {
      robotsValue = metaRobotsValue({ noindex: true, nofollow: true });
    }

    return {
      /**
       * Capitalize because it can start with collection placeholder which is lowercase.
       */
      title: capitalize(seoTitle ?? title ?? 'Fixter'),
      description: capitalize(metaDescription ?? description ?? ''),
      keywords: metaKeywords?.length ? metaKeywords.join(',') : keywords,
      robots: robotsValue || setRobots(robots),
    };
  }, [description, keywords, location.hostname, productionDomain, robots, seo, title]);

  return (
    <Head>
      <title>{seoMeta.title}</title>
      {seoMeta.description && <meta name="description" content={seoMeta.description} />}
      {seoMeta.keywords && <meta name="keywords" content={seoMeta.keywords} />}
      <meta name="robots" content={seoMeta.robots} />

      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      {alternateLinks.map(({ hrefLang, href }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}

      <meta name="theme-color" content="#ffffff" />
      <link rel="apple-touch-icon" href="/manifest/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest/site.webmanifest" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(snippet) }} />

      <meta property="fb:app_id" content="458404274495046" />
      <meta property="og:site_name" content="Fixter" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={facebookUrl} />

      <meta
        property="og:image"
        content="https://res.cloudinary.com/fxtr/image/upload/v1622815951/landing/icon_fixter-logo_kdhluo.svg"
      />

      {/*
        @NOTE do not render canonical link tag at all if the canonical is undefined

        This allows Google to infer what the canonical is rather than use a blank
        tag
      */}
      {canonical && <link rel="canonical" href={canonical} />}

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:creator" content={twitterCreator} />
      <meta
        name="twitter:image"
        content="https://res.cloudinary.com/fxtr/image/upload/v1622815951/landing/icon_fixter-logo_kdhluo.svg"
      />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {facebook && <meta name="facebook-domain-verification" content={facebook} />}

      {geoMeta && (
        <>
          <meta name="geo.position" content={geoMeta} />
          <meta name="ICBM" content={geoMeta} />
          <meta name="geo.placename" content={areaName} />
        </>
      )}

      {/* Preload above the fold common resources */}
      {/* This IS WORKING but is being removed from the DOM tree after render; this is likely a memory optimisation from Next */}
      <ResourceHints resources={FONT_PRELOADS} />
    </Head>
  );
}
